import { 
  VALIDATION_RULES, 
  ERROR_MESSAGES, 
  SUCCESS_MESSAGES,
  USER_STATUSES,
  USER_ROLES,
  REPORT_STATUSES,
  COMPLAINT_STATUSES,
  PLAN_TIERS
} from './types';

/**
 * Utility functions for Admin Services
 */

// ===== VALIDATION FUNCTIONS =====

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} Is valid email
 */
export const isValidEmail = (email) => {
  return VALIDATION_RULES.EMAIL_REGEX.test(email);
};

/**
 * Validate phone number format
 * @param {string} phone - Phone number to validate
 * @returns {boolean} Is valid phone number
 */
export const isValidPhone = (phone) => {
  return VALIDATION_RULES.PHONE_REGEX.test(phone);
};

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validatePassword = (password) => {
  const errors = [];
  
  if (!password || password.length < VALIDATION_RULES.PASSWORD_MIN_LENGTH) {
    errors.push(ERROR_MESSAGES.PASSWORD_TOO_SHORT);
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  
  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate username
 * @param {string} username - Username to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateUsername = (username) => {
  const errors = [];
  
  if (!username || username.length < VALIDATION_RULES.USERNAME_MIN_LENGTH) {
    errors.push(ERROR_MESSAGES.USERNAME_TOO_SHORT);
  }
  
  if (username.length > VALIDATION_RULES.USERNAME_MAX_LENGTH) {
    errors.push(ERROR_MESSAGES.USERNAME_TOO_LONG);
  }
  
  if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
    errors.push('Username can only contain letters, numbers, underscores, and hyphens');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate required fields
 * @param {Object} data - Data object to validate
 * @param {Array} requiredFields - Array of required field names
 * @returns {Object} Validation result with isValid and errors
 */
export const validateRequiredFields = (data, requiredFields) => {
  const errors = [];
  
  requiredFields.forEach(field => {
    if (!data[field] || (typeof data[field] === 'string' && data[field].trim() === '')) {
      errors.push(`${field} is required`);
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate field length
 * @param {string} value - Value to validate
 * @param {string} fieldName - Name of the field for error message
 * @param {number} maxLength - Maximum allowed length
 * @returns {Object} Validation result with isValid and errors
 */
export const validateFieldLength = (value, fieldName, maxLength) => {
  if (value && value.length > maxLength) {
    return {
      isValid: false,
      errors: [`${fieldName} must be less than ${maxLength} characters`]
    };
  }
  
  return { isValid: true, errors: [] };
};

// ===== STATUS VALIDATION =====

/**
 * Validate user status
 * @param {string} status - Status to validate
 * @returns {boolean} Is valid status
 */
export const isValidUserStatus = (status) => {
  return Object.values(USER_STATUSES).includes(status);
};

/**
 * Validate user role
 * @param {string} role - Role to validate
 * @returns {boolean} Is valid role
 */
export const isValidUserRole = (role) => {
  return Object.values(USER_ROLES).includes(role);
};

/**
 * Validate report status
 * @param {string} status - Status to validate
 * @returns {boolean} Is valid status
 */
export const isValidReportStatus = (status) => {
  return Object.values(REPORT_STATUSES).includes(status);
};

/**
 * Validate complaint status
 * @param {string} status - Status to validate
 * @returns {boolean} Is valid status
 */
export const isValidComplaintStatus = (status) => {
  return Object.values(COMPLAINT_STATUSES).includes(status);
};

/**
 * Validate plan tier
 * @param {string} tier - Tier to validate
 * @returns {boolean} Is valid tier
 */
export const isValidPlanTier = (tier) => {
  return Object.values(PLAN_TIERS).includes(tier);
};

// ===== ERROR HANDLING =====

/**
 * Create standardized error response
 * @param {string} message - Error message
 * @param {string} code - Error code
 * @param {*} details - Additional error details
 * @returns {Object} Standardized error response
 */
export const createErrorResponse = (message, code = 'ERROR', details = null) => {
  return {
    success: false,
    error: {
      message,
      code,
      details,
      timestamp: new Date().toISOString()
    }
  };
};

/**
 * Create standardized success response
 * @param {*} data - Response data
 * @param {string} message - Success message
 * @returns {Object} Standardized success response
 */
export const createSuccessResponse = (data, message = 'Operation successful') => {
  return {
    success: true,
    data,
    message,
    timestamp: new Date().toISOString()
  };
};

/**
 * Handle Firebase errors and return user-friendly messages
 * @param {Error} error - Firebase error
 * @returns {Object} Standardized error response
 */
export const handleFirebaseError = (error) => {
  console.error('Firebase Error:', error);
  
  let message = ERROR_MESSAGES.OPERATION_FAILED;
  let code = 'FIREBASE_ERROR';
  
  if (error.code) {
    switch (error.code) {
      case 'permission-denied':
        message = ERROR_MESSAGES.INSUFFICIENT_PERMISSIONS;
        code = 'PERMISSION_DENIED';
        break;
      case 'not-found':
        message = ERROR_MESSAGES.DOCUMENT_NOT_FOUND;
        code = 'NOT_FOUND';
        break;
      case 'already-exists':
        message = 'Document already exists';
        code = 'ALREADY_EXISTS';
        break;
      case 'invalid-argument':
        message = ERROR_MESSAGES.VALIDATION_ERROR;
        code = 'INVALID_ARGUMENT';
        break;
      case 'unavailable':
        message = ERROR_MESSAGES.NETWORK_ERROR;
        code = 'UNAVAILABLE';
        break;
      case 'deadline-exceeded':
        message = ERROR_MESSAGES.TIMEOUT_ERROR;
        code = 'TIMEOUT';
        break;
      default:
        message = error.message || ERROR_MESSAGES.OPERATION_FAILED;
    }
  }
  
  return createErrorResponse(message, code, error);
};

// ===== DATA TRANSFORMATION =====

/**
 * Transform Firebase timestamp to readable date
 * @param {Object} timestamp - Firebase timestamp
 * @returns {string} Formatted date string
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  try {
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString('en-GB');
    }
    return new Date(timestamp).toLocaleDateString('en-GB');
  } catch (error) {
    return 'Invalid Date';
  }
};

/**
 * Transform Firebase timestamp to ISO string
 * @param {Object} timestamp - Firebase timestamp
 * @returns {string} ISO date string
 */
export const timestampToISO = (timestamp) => {
  if (!timestamp) return null;
  
  try {
    if (timestamp.toDate) {
      return timestamp.toDate().toISOString();
    }
    return new Date(timestamp).toISOString();
  } catch (error) {
    return null;
  }
};

/**
 * Clean object by removing undefined and null values
 * @param {Object} obj - Object to clean
 * @returns {Object} Cleaned object
 */
export const cleanObject = (obj) => {
  const cleaned = {};
  
  Object.keys(obj).forEach(key => {
    if (obj[key] !== undefined && obj[key] !== null) {
      cleaned[key] = obj[key];
    }
  });
  
  return cleaned;
};

/**
 * Deep clone object (simple implementation)
 * @param {Object} obj - Object to clone
 * @returns {Object} Cloned object
 */
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime());
  if (obj instanceof Array) return obj.map(item => deepClone(item));
  
  const cloned = {};
  Object.keys(obj).forEach(key => {
    cloned[key] = deepClone(obj[key]);
  });
  
  return cloned;
};

// ===== ARRAY UTILITIES =====

/**
 * Remove duplicates from array
 * @param {Array} array - Array to deduplicate
 * @param {string} key - Key to use for deduplication
 * @returns {Array} Deduplicated array
 */
export const removeDuplicates = (array, key = null) => {
  if (!key) {
    return [...new Set(array)];
  }
  
  const seen = new Set();
  return array.filter(item => {
    const value = item[key];
    if (seen.has(value)) {
      return false;
    }
    seen.add(value);
    return true;
  });
};

/**
 * Sort array by multiple fields
 * @param {Array} array - Array to sort
 * @param {Array} sortFields - Array of sort field objects
 * @returns {Array} Sorted array
 */
export const sortByMultipleFields = (array, sortFields) => {
  return array.sort((a, b) => {
    for (const field of sortFields) {
      const { key, direction = 'asc' } = field;
      const aVal = a[key];
      const bVal = b[key];
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    }
    return 0;
  });
};

// ===== STRING UTILITIES =====

/**
 * Capitalize first letter of each word
 * @param {string} str - String to capitalize
 * @returns {string} Capitalized string
 */
export const capitalizeWords = (str) => {
  if (!str) return '';
  return str.replace(/\w\S*/g, txt => 
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
};

/**
 * Generate slug from string
 * @param {string} str - String to convert to slug
 * @returns {string} Slug string
 */
export const generateSlug = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

/**
 * Truncate string to specified length
 * @param {string} str - String to truncate
 * @param {number} length - Maximum length
 * @param {string} suffix - Suffix to add if truncated
 * @returns {string} Truncated string
 */
export const truncateString = (str, length, suffix = '...') => {
  if (!str || str.length <= length) return str;
  return str.substring(0, length - suffix.length) + suffix;
}; 